import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { waybillAPI } from '../../api/waybillAPI';
import useCompanyContext from '../../hooks/useCompanyContext';

const PAGE_SIZE = 25;

const fmtCurrency = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';

const fmtLong = (dateStr) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

function exportCSV(waybills) {
  const headers = ['#', 'Serial No.', 'Shipper', 'Customer', 'Date', 'Origin', 'Destination', 'Delivered', 'Shipment Type', 'Amount Due'];
  const rows = waybills.map((w, i) => [
    i + 1,
    w.SerialNo || '',
    w.Shipper || '',
    w.CustomerId?.CustomerName || w.CustomerId?.name || w.CustomerName || '',
    fmtDate(w.TransactionDate),
    w.Origin || '',
    w.Destination || '',
    w.IsDelivered ? 'Delivered' : 'Pending',
    w.ShipmentType || w.shipmentType || '',
    typeof w.amountDue === 'number' ? w.amountDue : '',
  ]);
  const total = waybills.reduce((s, w) => s + Math.round((typeof w.amountDue === 'number' ? w.amountDue : 0) * 100), 0) / 100;
  const totalRow = ['', '', '', '', '', '', '', '', 'TOTAL', total];
  const csv = [headers, ...rows, totalRow]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `daily-waybill-report.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DailyWaybillReportPage() {
  const navigate = useNavigate();
  const { accountingClientId, contextKind } = useCompanyContext();
  const todayStr = new Date().toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [shipmentType, setShipmentType] = useState('');
  const [search, setSearch] = useState('');
  const [waybills, setWaybills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);

  const loadReport = async (from = dateFrom, to = dateTo, type = shipmentType) => {
    setLoading(true);
    try {
      const clientId = accountingClientId || localStorage.getItem('accountingClientId') || undefined;
      const res = await waybillAPI.getDailyWaybill({ dateFrom: from, dateTo: to, shipmentType: type, ...(clientId ? { accountingClient: clientId } : {}) });
      setWaybills(Array.isArray(res.data?.data) ? res.data.data : []);
      setPage(1);
      setLoaded(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, [accountingClientId, contextKind]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [search]);

  const handleRowClick = (w) => {
    const type = w.ShipmentType || w.shipmentType || '';
    navigate(type === 'trucking'
      ? `/billing/trucking-waybill/${w._id}/edit`
      : `/billing/waybill/${w._id}/edit`);
  };

  const handlePrint = () => {
    const params = new URLSearchParams({ dateFrom, dateTo, shipmentType });
    window.open(`/billing/reports/print/daily-waybill?${params}`, '_blank');
  };

  const dateLabel = dateFrom === dateTo
    ? fmtLong(dateFrom)
    : `${fmtLong(dateFrom)} – ${fmtLong(dateTo)}`;

  const filteredWaybills = search.trim()
    ? waybills.filter((w) => {
        const q = search.trim().toLowerCase();
        const customer = (w.CustomerId?.CustomerName || w.CustomerId?.name || w.CustomerName || '').toLowerCase();
        return (
          (w.SerialNo         || '').toLowerCase().includes(q) ||
          (w.referenceNumber  || '').toLowerCase().includes(q) ||
          (w.Shipper          || '').toLowerCase().includes(q) ||
          customer.includes(q)
        );
      })
    : waybills;

  const totalPages = Math.max(1, Math.ceil(filteredWaybills.length / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const pageWaybills = filteredWaybills.slice(startIdx, startIdx + PAGE_SIZE);
  const periodTotal  = filteredWaybills.reduce((s, w) => s + Math.round((typeof w.amountDue === 'number' ? w.amountDue : 0) * 100), 0) / 100;
  const pageSubtotal = pageWaybills.reduce((s, w) => s + Math.round((typeof w.amountDue === 'number' ? w.amountDue : 0) * 100), 0) / 100;

  const pageNumbers = Array.from({ length: totalPages }, (_, idx) => idx + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  return (
    <Layout pageTitle="Daily Waybill Report">
      <div className="space-y-4 max-w-6xl">
        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-4 bg-white rounded-lg shadow px-4 py-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
            <input type="date" value={dateFrom} max={dateTo}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
            <input type="date" value={dateTo} min={dateFrom}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Shipment Type</label>
            <select value={shipmentType} onChange={(e) => setShipmentType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All</option>
              <option value="freightforwarding">Freight Forwarding</option>
              <option value="trucking">Trucking</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Serial no., ref no., shipper…"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <button onClick={() => loadReport()} disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
            {loading ? 'Loading…' : 'Load Report'}
          </button>
          {loaded && waybills.length > 0 && (
            <>
              <button onClick={handlePrint}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                Print
              </button>
              <button onClick={() => exportCSV(waybills)}
                className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium hover:bg-green-100">
                Export CSV
              </button>
            </>
          )}
        </div>

        {/* Summary strip */}
        {loaded && (
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 bg-white rounded-lg shadow px-4 py-3">
            <span>Period: <strong>{dateLabel}</strong></span>
            <span>Total: <strong>{filteredWaybills.length}</strong>{search && waybills.length !== filteredWaybills.length && <span className="text-gray-400 font-normal"> of {waybills.length}</span>}</span>
            <span>Delivered: <strong className="text-green-600">{filteredWaybills.filter((w) => w.IsDelivered).length}</strong></span>
            <span>Pending: <strong className="text-gray-500">{filteredWaybills.filter((w) => !w.IsDelivered).length}</strong></span>
            {filteredWaybills.length > 0 && (
              <span>Total Amount Due: <strong className="text-teal-700">{fmtCurrency(periodTotal)}</strong></span>
            )}
          </div>
        )}

        {/* Table */}
        {loaded && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Serial No.', 'Shipper', 'Customer', 'Date', 'Origin', 'Destination', 'Delivered', 'Amount Due'].map((h) => (
                    <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase whitespace-nowrap ${h === 'Amount Due' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageWaybills.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                    {search ? `No waybills matching "${search}".` : 'No waybills found.'}
                  </td></tr>
                ) : pageWaybills.map((w, i) => {
                  const custName = w.CustomerId?.CustomerName || w.CustomerId?.name || w.CustomerName || '—';
                  return (
                    <tr key={w._id} onClick={() => handleRowClick(w)} className="hover:bg-teal-50 cursor-pointer">
                      <td className="px-4 py-2 text-gray-500">{startIdx + i + 1}</td>
                      <td className="px-4 py-2 font-medium text-teal-700">{w.SerialNo}</td>
                      <td className="px-4 py-2">{w.Shipper || '—'}</td>
                      <td className="px-4 py-2">{custName}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(w.TransactionDate)}</td>
                      <td className="px-4 py-2">{w.Origin || '—'}</td>
                      <td className="px-4 py-2">{w.Destination || '—'}</td>
                      <td className="px-4 py-2">
                        {w.IsDelivered
                          ? <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Delivered</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Pending</span>}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtCurrency(w.amountDue)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {pageWaybills.length > 0 && (
                <tfoot>
                  {totalPages > 1 && (
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td colSpan={8} className="px-4 py-2 text-xs font-medium text-gray-500 text-right uppercase tracking-wide">
                        Page {page} Subtotal
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-700">{fmtCurrency(pageSubtotal)}</td>
                    </tr>
                  )}
                  <tr className="bg-teal-50 border-t-2 border-teal-200">
                    <td colSpan={8} className="px-4 py-2 text-xs font-bold text-teal-800 text-right uppercase tracking-wide">
                      Period Total
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold text-teal-800">{fmtCurrency(periodTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Pagination controls */}
        {loaded && totalPages > 1 && (
          <div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3 text-sm">
            <span className="text-gray-500">
              Showing {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filteredWaybills.length)} of {filteredWaybills.length} waybill{filteredWaybills.length !== 1 ? 's' : ''}
              {search && waybills.length !== filteredWaybills.length && ` (filtered from ${waybills.length})`}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                ← Prev
              </button>
              {pageNumbers.map((item, idx) =>
                item === '…' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setPage(item)}
                    className={`px-3 py-1.5 border rounded text-sm ${item === page ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    {item}
                  </button>
                )
              )}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
